import React, {useEffect, useMemo} from 'react';
import {View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react';

import Text from '../../components/ui-controls/Text';
import {useSearchDispatch} from '../../context/SearchContext';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {findSubDepartment} from '../../navigation/linking';
import {isViewAll} from '../../navigation/viewAllSubDepartment';
import {useStores} from '../../stores/use-stores';
import {codeifyName} from '../../utils/string';

import RangeProductListScreen from './RangeProductListScreen';

const SubDepartmentScreen = observer(
  ({route}: RouteNavigatorScreenProps<AppRoute.SUB_DEPARTMENT>) => {
    const {rangeStore, filterStore} = useStores();
    const searchDispatch = useSearchDispatch();

    // Prepare department/subdepartment from route params
    // Guard against undefined params (can happen during deep link parsing before data loads)
    const {department: deptParam, subDepartment: subDeptParam} = route.params ?? {};
    // The root department ("All"): RangeStore's first department with no parent.
    const rootId = rangeStore.parentDepartment?.id;

    const {department, subDepartment} = useMemo(() => {
      // "View All" (both shapes, see isViewAll). The tile's parent is the department's parent, so
      // it must be caught here, before the re-scope below finds no "view-all" child and renders
      // not-found. The name check matters: a refresh of fortified-wine/fortified-wine parses both
      // params to one department.
      if (isViewAll(deptParam, subDeptParam)) {
        return {
          department: {...deptParam, name: 'View All'},
          subDepartment: undefined,
        };
      }
      // A deep link / web refresh parses each param by name alone, so a shared name can
      // resolve to the wrong department: a sub-department (Gin) under the wrong parent, or
      // the parent itself (Fortified Wine) to its same-name child. Resolve the URL parent
      // among the root's children first (R65), then re-scope the sub-department to it.
      if (subDeptParam && deptParam?.id && subDeptParam.parent?.id !== deptParam.id) {
        const parent =
          (rootId && findSubDepartment(rootId, codeifyName(deptParam.name))) || deptParam;
        return {
          department: parent,
          subDepartment: findSubDepartment(parent.id, codeifyName(subDeptParam.name)),
        };
      }
      return {department: deptParam, subDepartment: subDeptParam};
    }, [deptParam, subDeptParam, rootId]);

    // Store mutations MUST be in useEffect, not during render
    useEffect(() => {
      rangeStore.selectDepartment(department);
      filterStore.clearSortAndFilters();
      if (subDepartment) {
        rangeStore.selectSubDepartment(subDepartment);
      }
      // Scope SearchContext to the active sub-department (or parent for "View All")
      const activeDeptId = subDepartment?.id ?? department?.id;
      if (activeDeptId) {
        filterStore.loadFilterOptions();
        searchDispatch({type: 'SCOPE_TO_DEPARTMENT', departmentId: activeDeptId});
      }
    }, [rangeStore, filterStore, department, subDepartment, searchDispatch]);

    // Re-establish department scope when this screen regains focus.
    // SearchScreen's CLEAR_ALL resets departmentId to null in the shared
    // SearchContext.  The mount useEffect above does NOT re-fire on
    // back-navigation (same route params).  SET_DEPARTMENT preserves
    // user-applied filters unlike SCOPE_TO_DEPARTMENT.
    useFocusEffect(
      React.useCallback(() => {
        const activeDeptId = subDepartment?.id ?? department?.id;
        if (activeDeptId) {
          searchDispatch({type: 'SET_DEPARTMENT', departmentId: activeDeptId});
        }
      }, [department?.id, subDepartment?.id, searchDispatch]),
    );

    if (!department || (department.name !== 'View All' && !subDepartment)) {
      return (
        <View>
          <Text>Category/SubCategory not found</Text>
        </View>
      );
    }

    // TODO: This depends on  rangeStore.selected... 🙄
    return <RangeProductListScreen />;
  },
);

export default SubDepartmentScreen;
